import { CheckCircleFilled, LoadingOutlined } from "@ant-design/icons";
import type React from "react";
import { useState } from "react";
import { useOptionalTransfer } from "../../TransferButton/TransferContext";

// Post-review suggestion card: orders extracted from the just-generated note,
// offered for one-tap placement. Confirm dispatches `place_suggested_orders`
// with the SELECTED orders verbatim — the backend runs the workflow directly
// (no chat-LLM round trip) and narrates progress into the thread.
//
// When the note hasn't been transferred yet (note_transferred=false), the
// primary action is "Transfer note & place orders": the existing transfer
// flow starts (banner / appointment / MFA modals unchanged), then
// `queue_orders_after_transfer` stores the orders on the job — the backend
// places them once the note lands, on the same encounter, even if the
// clinician navigates away.
//
// Corrections happen in chat ("drop the lipid panel"): the backend re-emits
// this widget with the revised list and the older card renders superseded.
// Extra fields (dx, med sig, cpt code…) ride along untouched — the card's
// payload goes to the workflow verbatim, so this type stays open.
export interface SuggestedOrder {
  test_name: string;
  order_type?: string;
  search_aliases?: string[];
  dx?: string;
  strength?: string;
  formulation?: string;
  take?: string;
  freq?: string;
  duration?: string;
  dispense?: string;
  refills?: string;
  code?: string;
  urgency?: string;
  fasting?: boolean;
}

// The sig line shown under a medication so the clinician sees exactly what
// will be placed ("500 mg — 1 tablet · Twice a day · 90 days · 2 refills").
const medDetail = (o: SuggestedOrder): string => {
  const sig = [o.take, o.freq, o.duration, o.refills ? `${o.refills} refills` : ""].filter(Boolean).join(" · ");
  return [o.strength, sig].filter(Boolean).join(" — ");
};

export interface OrderSuggestionsData {
  audio_file_id?: number;
  ehr_label?: string;
  question?: string;
  orders?: SuggestedOrder[];
  note_transferred?: boolean;
}

interface OrderSuggestionsProps {
  data: OrderSuggestionsData;
  onCustomAction: (action: { type: string; payload?: Record<string, unknown> }) => void;
  disabled?: boolean;
  // A newer order_suggestions card exists in the thread (a chat revision) —
  // render collapsed so two interactive lists never compete.
  superseded?: boolean;
}

type Phase = "input" | "submitted" | "combined_submitted" | "dismissed";

const ORDER_TYPE_BADGES: Record<string, { label: string; color: string; bg: string }> = {
  lab: { label: "Lab", color: "#0E7490", bg: "#ECFEFF" },
  di: { label: "Imaging", color: "#6D28D9", bg: "#F5F3FF" },
  procedure: { label: "Procedure", color: "#B45309", bg: "#FFFBEB" },
  medication: { label: "Medication", color: "#BE185D", bg: "#FDF2F8" },
  cpt: { label: "Visit code", color: "#166534", bg: "#F0FDF4" },
};

const BoltIcon = () => (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, display: "block" }}>
    <path
      d="M8.5 1.5 3 9h4l-.5 5.5L13 7H8.5l0-5.5Z"
      fill="#EEF2FF"
      stroke="#4F46E5"
      strokeWidth="1.3"
      strokeLinejoin="round"
    />
  </svg>
);

const OrderSuggestions: React.FC<OrderSuggestionsProps> = ({ data, onCustomAction, disabled, superseded }) => {
  const orders = data?.orders ?? [];
  const transfer = useOptionalTransfer();
  const [phase, setPhase] = useState<Phase>("input");
  const [isStartingTransfer, setIsStartingTransfer] = useState(false);
  // All checked by default — the card exists because the note says to order
  // these. Keyed by index, not name: two same-named orders must not collide.
  const [selected, setSelected] = useState<Set<number>>(() => new Set(orders.map((_, i) => i)));

  if (!orders.length) return null;

  if (superseded) {
    return (
      <div style={{ ...styles.container, ...styles.mutedBox }}>
        <span style={styles.mutedText}>Updated — see the newer list below.</span>
      </div>
    );
  }

  const noteTransferred = !!data.note_transferred;
  const canCombine = !noteTransferred && !!transfer && !!data.audio_file_id;

  const toggle = (index: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });

  const selectedOrders = () => orders.filter((_, i) => selected.has(i));

  const place = () => {
    if (disabled || !selected.size) return;
    onCustomAction({
      type: "place_suggested_orders",
      payload: {
        audio_file_id: data.audio_file_id,
        ehr_label: data.ehr_label,
        orders: selectedOrders(),
      },
    });
    setPhase("submitted");
  };

  const transferAndPlace = async () => {
    if (disabled || !selected.size || !transfer || !data.audio_file_id || isStartingTransfer) return;
    setIsStartingTransfer(true);
    try {
      const result = await transfer.startTransfer(data.audio_file_id);
      if (result.jobId) {
        // A job id comes back for a fresh transfer, one already in flight
        // ("in_progress"), AND an already-completed one
        // ("already_transferred") — the backend chains the orders behind
        // that job, placing immediately when it's already done.
        onCustomAction({
          type: "queue_orders_after_transfer",
          payload: {
            audio_file_id: data.audio_file_id,
            transfer_job_id: result.jobId,
            orders: selectedOrders(),
          },
        });
        // Don't promise a transfer that isn't happening: for a completed
        // job the orders are being placed right now.
        setPhase(result.code === "already_transferred" ? "submitted" : "combined_submitted");
      }
      // No job id means the transfer couldn't start; startTransfer already
      // surfaced the error toast and the card stays interactive for a retry.
    } finally {
      setIsStartingTransfer(false);
    }
  };

  const dismiss = () => {
    if (disabled) return;
    onCustomAction({ type: "order_suggestions_dismiss" });
    setPhase("dismissed");
  };

  if (phase === "submitted") {
    return (
      <div style={{ ...styles.container, ...styles.resolvedBox }}>
        <div style={styles.statusRow}>
          <CheckCircleFilled style={{ color: "#10B981", fontSize: 16, marginTop: 1 }} />
          <span style={styles.resolvedText}>On it — I'll keep you posted in the chat.</span>
        </div>
      </div>
    );
  }

  if (phase === "combined_submitted") {
    return (
      <div style={{ ...styles.container, ...styles.resolvedBox }}>
        <div style={styles.statusRow}>
          <LoadingOutlined spin style={{ color: "#10B981", fontSize: 15, marginTop: 2 }} />
          <span style={styles.resolvedText}>
            Transferring the note first, then placing the orders — you can move on.
          </span>
        </div>
      </div>
    );
  }

  if (phase === "dismissed") {
    return (
      <div style={{ ...styles.container, ...styles.mutedBox }}>
        <span style={styles.mutedText}>Dismissed — nothing was ordered.</span>
      </div>
    );
  }

  const busy = disabled || isStartingTransfer;
  const count = selected.size;
  // Live count on the primary action ("place 2 orders") so partial selections
  // read back exactly what will happen; no count while nothing is selected
  // (the button is disabled then anyway).
  const countedOrders = count ? `${count} ${count === 1 ? "order" : "orders"}` : "orders";
  const primaryDisabled = busy || !count;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.iconBadge}>
          <BoltIcon />
        </div>
        <span style={styles.headerText}>
          {data.question || `This note's plan includes orders I can place in ${data.ehr_label || "your EHR"}:`}
        </span>
      </div>

      <div style={styles.orderList}>
        {orders.map((o, i) => {
          const badge = o.order_type ? ORDER_TYPE_BADGES[o.order_type] : undefined;
          const checked = selected.has(i);
          const detail = o.order_type === "medication" ? medDetail(o) : "";
          return (
            <label
              key={`${o.test_name}#${i}`}
              style={{
                ...styles.orderRow,
                ...(i > 0 ? styles.orderRowDivider : {}),
                ...(busy ? styles.orderRowBusy : {}),
              }}
            >
              <input
                type="checkbox"
                disabled={busy}
                checked={checked}
                onChange={() => toggle(i)}
                style={styles.checkbox}
              />
              <span style={{ ...styles.orderTextCol }}>
                <span style={{ ...styles.orderName, ...(checked ? {} : styles.orderNameUnchecked) }}>
                  {o.test_name}
                </span>
                {detail && (
                  <span style={{ ...styles.orderDetail, ...(checked ? {} : styles.orderNameUnchecked) }}>{detail}</span>
                )}
              </span>
              {badge && (
                <span style={{ ...styles.typeBadge, color: badge.color, backgroundColor: badge.bg }}>
                  {badge.label}
                </span>
              )}
            </label>
          );
        })}
      </div>

      <div style={styles.hint}>Need changes? Tell me below and I'll update this list.</div>

      <div style={styles.actions}>
        {canCombine ? (
          <>
            <button
              type="button"
              disabled={primaryDisabled}
              onClick={transferAndPlace}
              style={{ ...styles.primaryButton, ...(primaryDisabled ? styles.buttonDisabled : {}) }}
            >
              {isStartingTransfer ? "Starting…" : `Transfer note & place ${countedOrders}`}
            </button>
            <div style={styles.secondaryRow}>
              <button
                type="button"
                disabled={primaryDisabled}
                onClick={place}
                style={{ ...styles.secondaryButton, ...(primaryDisabled ? styles.buttonDisabled : {}) }}
              >
                {count === 1 ? "Place order only" : "Place orders only"}
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={dismiss}
                style={{ ...styles.ghostButton, ...(busy ? styles.buttonDisabled : {}) }}
              >
                Not now
              </button>
            </div>
          </>
        ) : (
          <div style={styles.secondaryRow}>
            <button
              type="button"
              disabled={primaryDisabled}
              onClick={place}
              style={{ ...styles.primaryButton, flex: 1, ...(primaryDisabled ? styles.buttonDisabled : {}) }}
            >
              {`Place ${countedOrders}`}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={dismiss}
              style={{ ...styles.ghostButton, ...(busy ? styles.buttonDisabled : {}) }}
            >
              Not now
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    padding: "14px",
    marginTop: "8px",
    borderRadius: "14px",
    border: "1px solid #E2E4F0",
    backgroundColor: "#FFFFFF",
    boxShadow: "0 1px 2px rgba(16, 24, 40, 0.05)",
    // Ancestors center-align text; every widget defends itself.
    textAlign: "left",
  },
  resolvedBox: { backgroundColor: "#F0FDF4", border: "1px solid #BBF7D0", boxShadow: "none" },
  mutedBox: { backgroundColor: "#F9FAFB", border: "1px solid #E5E7EB", boxShadow: "none", padding: "10px 14px" },
  mutedText: { fontSize: "12.5px", fontWeight: 500, color: "#6B7280", lineHeight: 1.4 },
  statusRow: { display: "flex", alignItems: "flex-start", gap: "9px" },
  resolvedText: { fontSize: "13px", fontWeight: 500, color: "#166534", lineHeight: 1.45 },
  header: { display: "flex", alignItems: "flex-start", gap: "9px" },
  iconBadge: {
    width: "26px",
    height: "26px",
    borderRadius: "8px",
    backgroundColor: "#EEF2FF",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  headerText: { fontSize: "13.5px", fontWeight: 600, color: "#111827", lineHeight: 1.45, paddingTop: "3px" },
  orderList: {
    display: "flex",
    flexDirection: "column",
    border: "1px solid #EEF0F4",
    borderRadius: "10px",
    backgroundColor: "#FBFBFD",
    overflow: "hidden",
  },
  orderRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
    padding: "9px 12px",
    cursor: "pointer",
  },
  orderRowDivider: { borderTop: "1px solid #F0F1F5" },
  orderRowBusy: { cursor: "default" },
  checkbox: {
    width: "15px",
    height: "15px",
    marginTop: "2px",
    flexShrink: 0,
    accentColor: "#4F46E5",
    cursor: "inherit",
  },
  orderTextCol: { flex: 1, display: "flex", flexDirection: "column", gap: "2px", minWidth: 0 },
  orderName: { fontSize: "13px", fontWeight: 500, color: "#1F2937", lineHeight: 1.45, minWidth: 0 },
  orderDetail: { fontSize: "12px", fontWeight: 400, color: "#6B7280", lineHeight: 1.4 },
  orderNameUnchecked: { color: "#9CA3AF", fontWeight: 400 },
  typeBadge: {
    fontSize: "11px",
    fontWeight: 600,
    lineHeight: 1,
    padding: "4px 8px",
    borderRadius: "999px",
    whiteSpace: "nowrap",
    flexShrink: 0,
    marginTop: "1px",
  },
  hint: { fontSize: "12px", color: "#9CA3AF", lineHeight: 1.4 },
  actions: { display: "flex", flexDirection: "column", gap: "8px" },
  secondaryRow: { display: "flex", gap: "8px", alignItems: "stretch" },
  primaryButton: {
    width: "100%",
    padding: "9px 14px",
    fontSize: "13px",
    fontWeight: 600,
    borderRadius: "10px",
    border: "1px solid #4F46E5",
    backgroundColor: "#4F46E5",
    color: "#FFFFFF",
    cursor: "pointer",
    fontFamily: "inherit",
    lineHeight: 1.4,
    boxSizing: "border-box",
  },
  secondaryButton: {
    flex: 1,
    padding: "8px 14px",
    fontSize: "13px",
    fontWeight: 500,
    borderRadius: "10px",
    border: "1px solid #D6D9E0",
    backgroundColor: "#FFFFFF",
    color: "#374151",
    cursor: "pointer",
    fontFamily: "inherit",
    lineHeight: 1.4,
    boxSizing: "border-box",
  },
  ghostButton: {
    flex: "0 0 auto",
    padding: "8px 12px",
    fontSize: "13px",
    fontWeight: 500,
    borderRadius: "10px",
    border: "1px solid transparent",
    backgroundColor: "transparent",
    color: "#6B7280",
    cursor: "pointer",
    fontFamily: "inherit",
    lineHeight: 1.4,
    boxSizing: "border-box",
  },
  buttonDisabled: { opacity: 0.55, cursor: "not-allowed" },
};

export default OrderSuggestions;
